import type { RawMqttMessage } from '@/lib/mqtt/types';
import type { ReactNode } from 'react';
import type { RateDisplay } from '../hooks/useMessageRate';
import type { DsBadge } from '../types';
import type { FieldFilter } from '../utils/payloadQuery';
import { FilterChips } from './FilterChips';
import { LiveStatusBar } from './LiveStatusBar';
import { MessageRow } from './MessageRow';
import { MessageSearch } from './MessageSearch';
import { TopicBreadcrumb } from './TopicBreadcrumb';

interface Props {
  topic: string | null;
  badge: DsBadge;
  /** Already narrowed by the active search/chips. */
  messages: RawMqttMessage[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  paused: boolean;
  /** Per-topic stats for the selected topic (total messages, msg/s, average
   *  payload size, last QoS) — not global firehose figures. */
  total: number;
  rate: RateDisplay;
  size: string;
  qos: number;
  /** Search/filter state. All optional so read-only consumers (Debug panel)
   *  can mount this pane unchanged. */
  filters?: readonly FieldFilter[];
  terms?: readonly string[];
  /** Messages held in the buffer before narrowing. */
  bufferTotal?: number;
  searchActive?: boolean;
  onRemoveFilter?: (id: string) => void;
  onReplaceFilter?: (oldId: string, next: FieldFilter) => void;
  onClearFilters?: () => void;
  /** Free-text search over this topic's messages. Rendered only when wired. */
  search?: string;
  onSearch?: (v: string) => void;
  /** `paused` was engaged by the search rather than by the Pause control. */
  autoPaused?: boolean;
  /** The "+ Filter" field-picker, built by the view from discovered fields. */
  builderSlot?: ReactNode;
}

const NO_FILTERS: FieldFilter[] = [];

export function MessageListPane({
  topic,
  badge,
  messages,
  selectedId,
  onSelect,
  paused,
  total,
  rate,
  size,
  qos,
  filters = NO_FILTERS,
  terms,
  bufferTotal,
  searchActive = false,
  onRemoveFilter,
  onReplaceFilter,
  onClearFilters,
  search,
  onSearch,
  autoPaused,
  builderSlot,
}: Props) {
  const leaf = topic ? topic.split('/').pop() : 'Messages';
  const buffered = bufferTotal ?? messages.length;
  const narrowing = searchActive || filters.length > 0;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center border-b border-(--color-border) bg-(--color-surface) px-3 py-[7px]">
        <span className="font-sans text-[1em] font-bold text-(--color-text)">{leaf}</span>
      </div>
      <TopicBreadcrumb topic={topic} />
      <LiveStatusBar paused={paused} total={total} rate={rate} qos={qos} size={size} />
      {search !== undefined && onSearch && (
        <MessageSearch
          value={search}
          onChange={onSearch}
          builderSlot={builderSlot}
          {...(autoPaused !== undefined ? { autoPaused } : {})}
        />
      )}
      {narrowing && onRemoveFilter && onReplaceFilter && onClearFilters && (
        <FilterChips
          filters={filters}
          onRemove={onRemoveFilter}
          onReplace={onReplaceFilter}
          onClear={onClearFilters}
          matched={messages.length}
          total={buffered}
          textActive={searchActive}
        />
      )}
      <div className="flex-1 overflow-y-auto">
        {messages.map((m) => (
          <MessageRow
            key={m.id}
            msg={m}
            badge={badge}
            selected={m.id === selectedId}
            onSelect={onSelect}
            terms={terms}
          />
        ))}
        {messages.length === 0 &&
          // Distinguish "nothing has arrived" from "your filter hid it all" —
          // otherwise an over-narrow filter reads as a dead topic.
          (narrowing && buffered > 0 ? (
            <div className="p-4 text-center font-sans text-[0.846em] text-(--color-text3)">
              <div>No matches in the last {buffered.toLocaleString()} messages</div>
              {onClearFilters && filters.length > 0 && (
                <button
                  type="button"
                  onClick={onClearFilters}
                  className="mt-1.5 cursor-pointer font-bold text-(--color-green) underline-offset-2 hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="p-4 text-center font-sans text-[0.846em] text-(--color-text3)">
              No messages on this topic yet
            </div>
          ))}
      </div>
    </div>
  );
}
